// src/components/parent-dashboard/modals/ProfilePictureModal.js
import React, { useRef, useState, useEffect } from "react";
import { X } from "lucide-react";
import { getInitials } from '../utils/formatters';

/**
 * Modal for uploading and updating child profile pictures
 */
const ProfilePictureModal = ({
    showProfilePictureModal,
    setShowProfilePictureModal,
    childProfilePicture,
    selectedChild,
    handleProfilePictureUpload,
    uploadingPicture
}) => {
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
        }
    };

    const handleUpload = () => {
        if (selectedFile) {
            handleProfilePictureUpload(selectedFile);
        }
    };

    const handleClose = () => {
        setShowProfilePictureModal(false);
        setSelectedFile(null);
        setPreviewUrl(null);
    };

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    if (!showProfilePictureModal) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-[#D67C2C]">Upload Profile Picture</h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600"
                        disabled={uploadingPicture}
                    >
                        <X size={24}/>
                    </button>
                </div>

                <div className="space-y-4">
                    {/* Current/Preview Image */}
                    <div className="flex justify-center">
                        <div className="relative">
                            {previewUrl ? (
                                <img
                                    src={previewUrl}
                                    alt="Preview"
                                    className="w-32 h-32 rounded-full object-cover border-4 border-[#F7D1B6]"
                                />
                            ) : childProfilePicture ? (
                                <img
                                    src={childProfilePicture}
                                    alt="Current"
                                    className="w-32 h-32 rounded-full object-cover border-4 border-[#F7D1B6]"
                                />
                            ) : (
                                <div
                                    className="w-32 h-32 rounded-full bg-[#FFF8F4] flex items-center justify-center text-4xl font-bold text-[#2173B7] border-4 border-[#F7D1B6]">
                                    {getInitials(selectedChild?.name || "?")}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* File Input */}
                    <div className="space-y-2">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            className="hidden"
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="w-full px-4 py-2 border-2 border-dashed border-[#2173B7] rounded-lg text-[#2173B7] hover:bg-[#FFF8F4] transition-colors"
                            disabled={uploadingPicture}
                        >
                            {selectedFile ? "Choose Different Picture" : "Choose Picture"}
                        </button>
                        {selectedFile && (
                            <p className="text-sm text-gray-600 text-center">
                                Selected: {selectedFile.name}
                            </p>
                        )}
                    </div>

                    {/* Upload Guidelines */}
                    <div className="text-xs text-gray-500 text-center space-y-1">
                        <p>Supported formats: JPEG, PNG, GIF</p>
                        <p>Maximum size: 5MB</p>
                        <p>Recommended: Square image for best fit</p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-3">
                        <button
                            onClick={handleClose}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            disabled={uploadingPicture}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleUpload}
                            disabled={!selectedFile || uploadingPicture}
                            className="flex-1 px-4 py-2 bg-[#2173B7] text-white rounded-lg hover:bg-[#e06b35] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                            {uploadingPicture ? (
                                <>
                                    <div
                                        className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                    Uploading...
                                </>
                            ) : (
                                "Upload Picture"
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePictureModal;